import MenuOutlinedIcon from '@mui/icons-material/MenuOutlined';
import { IconButton } from '@mui/material';
import type { MenuProps } from 'antd';
import { Avatar, Dropdown } from 'antd';
import clsx from 'clsx';
import { baseApiEndpoint } from 'config';
import { useLogout } from 'features/auth/hooks/useLogout';
import { NotificationPanel } from 'features/notification/components/NotificationPanel';
import { currentUserSelector } from 'features/user/selectors';
import { useAppSelector } from 'lib/stores/hook';
import { memo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getPath } from 'routes/router-paths';
import { AppBreadcrumbs } from 'shared/components/AppBreadcrumbs/AppBreadcrumbs';
import {
  AltArrowDownOutlineIcon,
  LogoutOutlineIcon,
  UserRoundedOutlineIcon,
} from 'shared/components/AppIcon';
import { routerUtils } from 'shared/utils';

type AppMainNavigationTabBarProps = {
  isSidebarCollapsed?: boolean;
  onSidebarExpand?: () => void;
};

export const AppMainNavigationTabBar = memo(
  ({ isSidebarCollapsed, onSidebarExpand }: AppMainNavigationTabBarProps) => {
    routerUtils.navigate = useNavigate();
    routerUtils.location = useLocation();

    const location = useLocation();
    const currentUser = useAppSelector(currentUserSelector);
    const { name_uppercase, position } = currentUser || {};

    const avatarUrl = currentUser?.image_id
      ? `${baseApiEndpoint}/images/${currentUser.image_id}`
      : undefined;

    const isHome = location.pathname === getPath('dashboardPage');

    const logout = useLogout();

    const menuItems: MenuProps['items'] = [
      {
        key: 'user-info',
        className: 'md:hidden',
        label: (
          <div className="flex items-center gap-2 py-1">
            <Avatar size={40} className="shrink-0" src={avatarUrl} />
            <div>
              <p className="text-sm font-medium capitalize text-black">
                {name_uppercase?.toLowerCase()}
              </p>
              <p className="text-xs text-black opacity-40">{position?.name}</p>
            </div>
          </div>
        ),
      },
      {
        type: 'divider',
        className: 'md:hidden',
      },
      {
        key: 'profile',
        label: 'Thông tin cá nhân',
        icon: <UserRoundedOutlineIcon className="h-5 w-5 text-primary" />,
      },
      {
        type: 'divider',
      },
      {
        key: 'logout',
        label: 'Đăng xuất',
        danger: true,
        icon: <LogoutOutlineIcon className="h-5 w-5" />,
      },
    ];

    const onMenuClick = useCallback(
      ({ key }: { key: string }) => {
        if (key === 'logout') {
          logout();
        }
      },
      [logout]
    );

    return (
      <div
        className={clsx(
          'flex items-center justify-between py-4',
          !isHome && 'border-b border-b-neutral-200'
        )}
      >
        <div className="flex items-center gap-4">
          {isSidebarCollapsed && onSidebarExpand ? (
            <IconButton
              onClick={onSidebarExpand}
              className="!h-9 !w-9 !rounded-lg !border !border-neutral-200 !bg-white !text-[#043871] hover:!bg-neutral-50"
              title="Mở rộng sidebar"
            >
              <MenuOutlinedIcon />
            </IconButton>
          ) : null}

          <div className="flex-1">
            <AppBreadcrumbs />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3">
          <NotificationPanel />

          <Dropdown
            menu={{ items: menuItems, onClick: onMenuClick }}
            trigger={['click']}
            placement="bottomRight"
          >
            <div className="flex cursor-pointer items-center justify-end gap-2 md:gap-5">
              <div className="flex items-center justify-end gap-2">
                <Avatar size={44} className="shrink-0" src={avatarUrl} />

                <div className="hidden md:block">
                  <p className="text-sm font-medium capitalize">
                    {name_uppercase?.toLowerCase()}
                  </p>
                  <p className="text-xs text-black opacity-40">
                    {position?.name}
                  </p>
                </div>
              </div>

              <AltArrowDownOutlineIcon className="hidden h-5 w-5 text-black md:block" />
            </div>
          </Dropdown>
        </div>
      </div>
    );
  }
);
